import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { persistGet, persistSet } from '@/lib/utils';
import { useAccounts } from '@/lib/api/hooks/useAccounts';
import { offsetMinutesForIana } from '@/utility/account-timezone-offset';
import { countryRegions } from '@/pages/amazon/accounts/Data/CountryRegions';

export type TimezoneStatus = 'loading' | 'ready' | 'unavailable';

export type SelectedRegion = {
  country: string;
  countrycode: string;
  flag: string;
  /** Canonical account IANA timezone. Absent until the account query settles. */
  timezone?: string;
  /** Derived compatibility offset in minutes. Not a timezone source. */
  timezoneOffset?: number;
  /** Same IANA identifier as timezone, for existing display consumers. */
  location?: string;
  timezoneStatus: TimezoneStatus;
  currencySymbol?: string;
  prefferedLanguage?: string;
  currencyCode?: string;
};

type RegionContextType = {
  selectedRegion: SelectedRegion;
  setSelectedRegion: (region: SelectedRegion) => void;
};

type AccountTimezoneRecord = {
  id: number | string;
  timezone?: string | null;
  isDefault?: boolean;
  countryCode?: string | null;
};

/** Region display fields for a marketplace country, or null if it is not a known marketplace. */
function regionForCountry(code: string): Partial<SelectedRegion> | null {
  for (const region of countryRegions) {
    const c = region.countries.find((country) => country.countryCode === code);
    if (c) {
      return {
        country: c.country,
        countrycode: c.countryCode,
        flag: c.flag,
        currencySymbol: c.currencySymbol,
        prefferedLanguage: c.prefferedLanguage,
        currencyCode: c.currencyCode,
      };
    }
  }
  return null;
}

const COUNTRY_PREF_KEY = 'app_v2_prefs_country';

const initialRegion: SelectedRegion = {
  country: 'India',
  countrycode: 'IN',
  flag: '🇮🇳',
  currencySymbol: '₹',
  prefferedLanguage: 'en-IN',
  currencyCode: 'INR',
  timezoneStatus: 'loading',
};

const noProviderRegion: SelectedRegion = {
  country: '',
  countrycode: '',
  flag: '',
  timezoneStatus: 'unavailable',
};

const RegionContext = createContext<RegionContextType | undefined>(undefined);

function countryPrefsFrom(region: Partial<SelectedRegion> | null | undefined): Pick<
  SelectedRegion,
  | 'country'
  | 'countrycode'
  | 'flag'
  | 'currencySymbol'
  | 'prefferedLanguage'
  | 'currencyCode'
> {
  return {
    country: region?.country ?? '',
    countrycode: region?.countrycode ?? '',
    flag: region?.flag ?? '',
    currencySymbol: region?.currencySymbol,
    prefferedLanguage: region?.prefferedLanguage,
    currencyCode: region?.currencyCode,
  };
}

function toAccountList(data: unknown): AccountTimezoneRecord[] {
  if (!Array.isArray(data)) return [];
  return data.filter(
    (item): item is AccountTimezoneRecord =>
      !!item && typeof item === 'object' && 'id' in item
  );
}

export function pickActiveAccount(
  accounts: AccountTimezoneRecord[]
): AccountTimezoneRecord | null {
  if (!accounts.length) return null;

  const storedId = localStorage.getItem('accountId');
  if (storedId) {
    const match = accounts.find((account) => String(account.id) === storedId);
    if (match) return match;
  }

  const preferred = accounts.find((account) => account.isDefault);
  if (preferred) return preferred;

  if (accounts.length === 1) return accounts[0];
  return null;
}

export const RegionProvider = ({ children }: { children: ReactNode }) => {
  const [selectedRegion, setSelectedRegion] =
    useState<SelectedRegion>(initialRegion);
  const { data, isPending, isError } = useAccounts();

  useEffect(() => {
    const stored = persistGet<Partial<SelectedRegion>>(COUNTRY_PREF_KEY);
    if (!stored?.countrycode) return;
    setSelectedRegion((prev) => ({
      ...prev,
      ...countryPrefsFrom(stored),
      timezoneStatus: prev.timezoneStatus,
      timezone: prev.timezone,
      timezoneOffset: prev.timezoneOffset,
      location: prev.location,
    }));
  }, []);

  useEffect(() => {
    persistSet(COUNTRY_PREF_KEY, countryPrefsFrom(selectedRegion));
  }, [selectedRegion]);

  useEffect(() => {
    if (isPending) {
      setSelectedRegion((prev) => ({
        ...countryPrefsFrom(prev),
        timezoneStatus: 'loading',
      }));
      return;
    }

    const accounts = isError ? [] : toAccountList(data);
    const active = pickActiveAccount(accounts);

    // The remembered country is a per-browser setting, so it can belong to another
    // login. If none of this user's accounts is in it, show their own account's country.
    const ownCodes = accounts
      .map((account) => account.countryCode)
      .filter((code): code is string => !!code);
    const fallbackCode = active?.countryCode || ownCodes[0];
    const countryFor = (prev: SelectedRegion) => {
      const kept = countryPrefsFrom(prev);
      if (!ownCodes.length || ownCodes.includes(kept.countrycode)) return kept;
      return { ...kept, ...(regionForCountry(fallbackCode) ?? {}) };
    };
    const timezone = active?.timezone?.trim() || '';
    const timezoneOffset = timezone ? offsetMinutesForIana(timezone) : null;

    if (!timezone || timezoneOffset === null) {
      setSelectedRegion((prev) => ({
        ...countryFor(prev),
        timezoneStatus: 'unavailable',
      }));
      return;
    }

    setSelectedRegion((prev) => ({
      ...countryFor(prev),
      timezone,
      location: timezone,
      timezoneOffset,
      timezoneStatus: 'ready',
    }));
  }, [data, isPending, isError]);

  return (
    <RegionContext.Provider value={{ selectedRegion, setSelectedRegion }}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (!context) {
    return {
      selectedRegion: noProviderRegion,
      setSelectedRegion: () => {},
    };
  }
  return context;
};
